import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import Endpoints from 'constants/endpoints';
import { HTTP_STATS_CODE } from 'constants/general';
import { toast } from 'react-toastify';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import {
  Project,
  ProjectListResponse,
  CreateProjectPayload,
  UpdateProjectPayload,
  InviteProjectMemberPayload,
  UpdateProjectMemberPayload,
  FetchProjectsParams,
  MoveProjectDetailsKanbanTaskPayload
} from 'interfaces/project.interface';
import { ToDoTask } from 'interfaces/common.interface';
import { RootState } from 'store';
import { v4 as uuid } from 'uuid';
import {
  mergeProjectDetailsTask,
  updateProjectDetailsIsCreatingTask,
  addProjectDetailsTaskStatusUpdatingId,
  removeProjectDetailsTaskStatusUpdatingId,
  updateProjectDetailsIsUpdatingTask,
  updateProjectDetailsSelectedTaskToEdit,
  updateProjectDetailsShowTaskDrawer
} from './slice';
import { fetchPortfolio } from 'store/reducer/portfolio/extra';
import { clearTopPriorityTaskForProject } from 'store/reducer/portfolio/slice';
import { ProjectLifecycleStatus, SORT_MODE } from 'enums';
import {
  buildProjectDetailsTaskPersistPayload,
  buildProjectDetailsTaskStatusToggle,
  buildProjectKanbanMovedTask,
  isProjectKanbanTaskInColumn
} from 'utils/projects/project-details.util';
import { isSyncedProject } from 'utils/todo.util';
import { uniqueProjectsById } from 'utils/projects/project.util';
import { isTaskWriteRefusal } from 'utils/projects/project-task-permissions.util';

/**
 * A refused task write is not a generic failure — tell the user it is a
 * permission so they stop retrying a click that cannot work.
 */
const notifyTaskWriteFailure = (error: unknown, title: string): void => {
  toast.error(
    isTaskWriteRefusal(error)
      ? t('project.task_write_refused', { title })
      : t('to_do_procrastinate.could_not_update_the_task', { title })
  );
};

const fetchProjectsList = async (params?: FetchProjectsParams) => {
  const queryParams: Record<string, string | number> = {};

  if (params?.page) {
    queryParams.page = params.page;
  }
  if (params?.take) {
    queryParams.take = params.take;
  }
  if (params?.order) {
    queryParams.order = params.order;
  }
  if (params?.completed_activity_id) {
    queryParams.completed_activity_id = params.completed_activity_id;
  }
  if (params?.tag_id) {
    queryParams.tag_id = params.tag_id;
  }
  if (params?.search) {
    queryParams.search = params.search;
  }
  if (params?.status) {
    queryParams.status = params.status;
  }

  const { data } = await focusBearApi.get<ProjectListResponse>(
    Endpoints.PROJECTS,
    { params: queryParams }
  );
  return data;
};

export const fetchProjects = createAsyncThunk(
  'project/fetch_projects',
  async (params?: FetchProjectsParams) => {
    try {
      return await fetchProjectsList(params);
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const fetchMoreProjects = createAsyncThunk(
  'project/fetch_more_projects',
  async (params: FetchProjectsParams) => {
    try {
      return await fetchProjectsList(params);
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const fetchAllProjects = createAsyncThunk(
  'project/fetch_all_projects',
  async () => {
    try {
      const all: Project[] = [];
      const take = 50; // backend @Max cap on PaginationOptionsDto.take
      const MAX_PAGES = 100; // guardrail against runaway loop on bad meta
      let page = 1;
      let meta: ProjectListResponse['meta'] | null = null;
      while (page <= MAX_PAGES) {
        const { data } = await focusBearApi.get<ProjectListResponse>(
          Endpoints.PROJECTS,
          {
            params: {
              page,
              take,
              status: ProjectLifecycleStatus.ACTIVE
            }
          }
        );
        all.push(...data.data);
        meta = data.meta;
        if (!data.meta.hasNextPage) break;
        page++;
      }
      return { data: uniqueProjectsById(all), meta };
    } catch (error) {
      captureException(error);
      return null;
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  'project/fetch_project_by_id',
  async (projectId: string) => {
    try {
      const { data } = await focusBearApi.get<Project>(
        Endpoints.PROJECT_BY_ID(projectId)
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const createProject = createAsyncThunk(
  'project/create_project',
  async (payload: CreateProjectPayload, { dispatch }) => {
    try {
      const { data, status } = await focusBearApi.post<Project>(
        Endpoints.PROJECTS,
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('project.created_successfully'));
        void dispatch(fetchPortfolio());
        return data;
      }
      toast.error(t('project.could_not_create'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const updateProject = createAsyncThunk(
  'project/update_project',
  async (
    {
      projectId,
      payload
    }: {
      projectId: string;
      payload: UpdateProjectPayload;
    },
    { dispatch }
  ) => {
    try {
      const { data, status } = await focusBearApi.put<Project>(
        Endpoints.PROJECT_BY_ID(projectId),
        payload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('project.updated_successfully'));
        void dispatch(fetchPortfolio());
        return data;
      }
      toast.error(t('project.could_not_update'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const deleteProject = createAsyncThunk(
  'project/delete_project',
  async (projectId: string, { dispatch }) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.PROJECT_BY_ID(projectId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('project.deleted_successfully'));
        dispatch(clearTopPriorityTaskForProject(projectId));
        void dispatch(fetchPortfolio());
        return projectId;
      }
      toast.error(t('project.could_not_delete'));
      return null;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const inviteProjectMember = createAsyncThunk(
  'project/invite_member',
  async ({
    projectId,
    payload
  }: {
    projectId: string;
    payload: InviteProjectMemberPayload;
  }) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.PROJECT_MEMBERS(projectId),
        payload
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('project.invitation_sent_successfully'));
        return true;
      }
      toast.error(t('project.could_not_send_invitation'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const removeProjectMember = createAsyncThunk(
  'project/remove_member',
  async ({ projectId, memberId }: { projectId: string; memberId: string }) => {
    try {
      const { status } = await focusBearApi.delete(
        Endpoints.PROJECT_MEMBER_BY_ID(projectId, memberId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('project.member_removed_successfully'));
        return true;
      }
      toast.error(t('project.could_not_remove_member'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const updateProjectMemberRole = createAsyncThunk(
  'project/update_member_role',
  async ({
    projectId,
    memberId,
    payload
  }: {
    projectId: string;
    memberId: string;
    payload: UpdateProjectMemberPayload;
  }) => {
    try {
      const { status } = await focusBearApi.put(
        Endpoints.PROJECT_MEMBER_BY_ID(projectId, memberId),
        payload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('project.member_role_updated_successfully'));
        return true;
      }
      toast.error(t('project.could_not_update_member_role'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const acceptProjectInvitation = createAsyncThunk(
  'project/accept_invitation',
  async (projectId: string) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.PROJECT_ACCEPT_INVITATION(projectId)
      );
      if (status === HTTP_STATS_CODE.CREATED) {
        toast.success(t('project.invitation_accepted_successfully'));
        return true;
      }
      toast.error(t('project.could_not_accept_invitation'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const leaveProject = createAsyncThunk(
  'project/leave_project',
  async (projectId: string, { dispatch }) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.PROJECT_LEAVE(projectId)
      );
      if (
        ![HTTP_STATS_CODE.SUCCESS, HTTP_STATS_CODE.NO_CONTENT].includes(status)
      ) {
        toast.error(t('project.could_not_leave'));
        return null;
      }
      toast.success(t('project.left_successfully'));
      dispatch(clearTopPriorityTaskForProject(projectId));
      void dispatch(fetchPortfolio());
      return projectId;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const declineProjectInvitation = createAsyncThunk(
  'project/decline_invitation',
  async (projectId: string) => {
    try {
      const { status } = await focusBearApi.post(
        Endpoints.PROJECT_DECLINE_INVITATION(projectId)
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        toast.success(t('project.invitation_declined_successfully'));
        return true;
      }
      toast.error(t('project.could_not_decline_invitation'));
      return false;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return false;
    }
  }
);

export const fetchPendingInvitations = createAsyncThunk(
  'project/fetch_pending_invitations',
  async () => {
    try {
      const { data } = await focusBearApi.get<Project[]>(
        Endpoints.PROJECT_PENDING_INVITATIONS
      );
      return data;
    } catch (error) {
      captureException(JSON.stringify(error));
      return [];
    }
  }
);

export const fetchProjectDetailsTasks = createAsyncThunk(
  'project/fetch_project_details_tasks',
  async (projectId: string, { getState }) => {
    try {
      const rootState = getState() as RootState;
      const sortMode =
        rootState.project.projectDetails.filters.sortMode ?? SORT_MODE.SMART;
      const currentProject = rootState.project.currentProject;

      const isSynced =
        currentProject?.id === projectId && isSyncedProject(currentProject);

      const params = {
        page: 1,
        take: 20,
        sort_mode: sortMode
      };

      if (isSynced) {
        const syncedProjectId = currentProject?.synced_project_id;
        if (!syncedProjectId) {
          return [];
        }
        const { data } = await focusBearApi.get(Endpoints.TO_DO, {
          params: {
            ...params,
            synced_project_id: syncedProjectId
          }
        });
        return data?.data ?? [];
      }

      const { data } = await focusBearApi.get(
        Endpoints.PROJECT_TODOS(projectId),
        { params }
      );
      return data?.data ?? [];
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    }
  }
);

export const createProjectDetailsTask = createAsyncThunk(
  'project/create_project_details_task',
  async (payload: Partial<ToDoTask>, { dispatch, getState }) => {
    try {
      dispatch(updateProjectDetailsIsCreatingTask(true));
      const rootState = getState() as RootState;
      const currentProject = rootState.project.currentProject;
      if (!currentProject) {
        return null;
      }

      const requestPayload = buildProjectDetailsTaskPersistPayload(
        { ...payload, id: payload.id ?? uuid() },
        currentProject
      );

      const { data, status } = await focusBearApi.put(
        Endpoints.TO_DO,
        requestPayload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        await dispatch(fetchProjectDetailsTasks(currentProject.id));
        dispatch(updateProjectDetailsShowTaskDrawer(false));
        dispatch(updateProjectDetailsSelectedTaskToEdit(null));
        return data;
      }
      return null;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    } finally {
      dispatch(updateProjectDetailsIsCreatingTask(false));
    }
  }
);

export const updateProjectDetailsTask = createAsyncThunk(
  'project/update_project_details_task',
  async (payload: Partial<ToDoTask>, { dispatch, getState }) => {
    try {
      dispatch(updateProjectDetailsIsUpdatingTask(true));
      const rootState = getState() as RootState;
      const currentProject = rootState.project.currentProject;
      if (!currentProject || !payload.id) {
        return null;
      }
      const requestPayload = buildProjectDetailsTaskPersistPayload(
        payload,
        currentProject
      );
      const { data, status } = await focusBearApi.put(
        Endpoints.TO_DO,
        requestPayload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        if (currentProject) {
          await dispatch(fetchProjectDetailsTasks(currentProject.id));
        }
        dispatch(updateProjectDetailsShowTaskDrawer(false));
        dispatch(updateProjectDetailsSelectedTaskToEdit(null));
        return data;
      }
      notifyTaskWriteFailure(null, payload.title ?? '');
      return null;
    } catch (error) {
      captureException(JSON.stringify(error));
      notifyTaskWriteFailure(error, payload.title ?? '');
      return null;
    } finally {
      dispatch(updateProjectDetailsIsUpdatingTask(false));
    }
  }
);

export const moveProjectDetailsKanbanTask = createAsyncThunk(
  'project/move_project_details_kanban_task',
  async (
    {
      task,
      targetColumnId,
      customStatuses = []
    }: MoveProjectDetailsKanbanTaskPayload,
    { dispatch, rejectWithValue, getState }
  ) => {
    const snapshot = task;

    if (
      isProjectKanbanTaskInColumn({
        task,
        columnId: targetColumnId,
        customStatuses
      })
    ) {
      return snapshot;
    }

    const updatedTask = buildProjectKanbanMovedTask({
      task,
      targetColumnId,
      customStatuses
    });

    dispatch(mergeProjectDetailsTask(updatedTask));

    try {
      dispatch(addProjectDetailsTaskStatusUpdatingId(task.id));
      const rootState = getState() as RootState;
      const currentProject = rootState.project.currentProject;
      if (!currentProject) {
        return rejectWithValue(null);
      }
      const requestPayload = buildProjectDetailsTaskPersistPayload(
        updatedTask,
        currentProject
      );
      const { data, status } = await focusBearApi.put(
        Endpoints.TO_DO,
        requestPayload
      );
      if (status === HTTP_STATS_CODE.SUCCESS) {
        dispatch(mergeProjectDetailsTask(data ?? updatedTask));
        return data ?? updatedTask;
      }

      dispatch(mergeProjectDetailsTask(snapshot));
      notifyTaskWriteFailure(null, task.title);
      return rejectWithValue(null);
    } catch (error) {
      captureException(JSON.stringify(error));
      dispatch(mergeProjectDetailsTask(snapshot));
      notifyTaskWriteFailure(error, task.title);
      return rejectWithValue(null);
    } finally {
      dispatch(removeProjectDetailsTaskStatusUpdatingId(task.id));
    }
  }
);

export const updateProjectDetailsTaskStatus = createAsyncThunk(
  'project/update_project_details_task_status',
  async (
    {
      task,
      isCompleted,
      restoreStatusId
    }: {
      task: ToDoTask;
      isCompleted: boolean;
      restoreStatusId?: string;
    },
    { dispatch, getState }
  ) => {
    try {
      dispatch(addProjectDetailsTaskStatusUpdatingId(task.id));
      const rootState = getState() as RootState;
      const currentProject = rootState.project.currentProject;
      if (!currentProject) {
        return null;
      }
      const statusFields = buildProjectDetailsTaskStatusToggle({
        isCompleted,
        customStatuses: currentProject.custom_statuses ?? [],
        restoreStatusId
      });
      if (!statusFields) {
        return null;
      }
      const requestPayload = buildProjectDetailsTaskPersistPayload(
        { ...task, ...statusFields },
        currentProject
      );
      const { data, status: responseStatus } = await focusBearApi.put(
        Endpoints.TO_DO,
        requestPayload
      );
      if (responseStatus === HTTP_STATS_CODE.SUCCESS) {
        if (currentProject) {
          await dispatch(fetchProjectDetailsTasks(currentProject.id));
        }
        return data;
      }
      return null;
    } catch (error) {
      captureException(JSON.stringify(error));
      return null;
    } finally {
      dispatch(removeProjectDetailsTaskStatusUpdatingId(task.id));
    }
  }
);

export const deleteProjectDetailsTask = createAsyncThunk(
  'project/delete_project_details_task',
  async (taskId: string, { dispatch, getState }) => {
    try {
      const currentProject = (getState() as RootState).project.currentProject;
      if (!currentProject) {
        return null;
      }
      const { status } = await focusBearApi.delete(Endpoints.TO_DO, {
        params: { todo_id: taskId }
      });
      if (status !== HTTP_STATS_CODE.SUCCESS) {
        toast.error(t('to_do_procrastinate.could_not_delete_the_task'));
        return null;
      }
      await dispatch(fetchProjectDetailsTasks(currentProject.id));
      dispatch(updateProjectDetailsShowTaskDrawer(false));
      dispatch(updateProjectDetailsSelectedTaskToEdit(null));
      return taskId;
    } catch (error) {
      toast.error(t('could_not_process_the_request'));
      captureException(JSON.stringify(error));
      return null;
    }
  }
);
